/* ==========================================================================
   OurPwr — scroll-driven emergency walkthrough (Three.js)

   Design constraints, in priority order:

   1. Never distress the visitor. No audio. No sudden motion. Nothing starts
      until the user scrolls here deliberately. The sequence shows the
      RESPONSE to an emergency, never the incident.
   2. Mobile-first performance. Three.js is imported only after the section
      is near the viewport, and only on devices that can handle it. Devices
      that can't get a calm static version, not a broken one.
   3. Swappable screens. Until real app snapshots exist, each beat's phone
      screen is drawn procedurally to canvas. Pass real image URLs via
      data-screens and they take over with no other change.

   The phone is deliberately generic hardware — no brand marks, no notch
   mimicry of any specific device.
   ========================================================================== */

const BEAT_COUNT = 6;

/* Beat indices, named so the animation code reads as narrative. */
const BEAT = {
  IDLE: 0,      // walking home, phone asleep
  PRESS: 1,     // the action button
  CIRCLE: 2,    // the circle is notified
  WITNESS: 3,   // digital witness: video, audio, location
  RESPONSE: 4,  // the circle responds
  VAULT: 5,     // afterwards: the evidence is already secured
};

/* -------------------------------------------------------------------------
   Capability detection
   ------------------------------------------------------------------------- */

function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function hasWebGL() {
  try {
    const canvas = document.createElement('canvas');
    return !!(window.WebGLRenderingContext &&
      (canvas.getContext('webgl2') || canvas.getContext('webgl')));
  } catch (e) {
    return false;
  }
}

/* Visibility is measured from the element rect rather than IntersectionObserver.
   IO is the more elegant tool, but a throttled or non-painting tab can leave its
   callback permanently unfired, and the failure mode there is a blank sticky
   void with no copy in it. A rect read is a few microseconds and always true. */
function isNear(el, margin) {
  const rect = el.getBoundingClientRect();
  return rect.bottom > -margin && rect.top < window.innerHeight + margin;
}

/* Cheap proxy for "this device will struggle". Erring toward the fallback is
   correct: a smooth static page beats a stuttering 3D one. */
function isLowPowered() {
  const cores = navigator.hardwareConcurrency;
  if (typeof cores === 'number' && cores > 0 && cores <= 2) return true;
  const mem = navigator.deviceMemory;
  if (typeof mem === 'number' && mem > 0 && mem < 2) return true;
  const conn = navigator.connection;
  if (conn && conn.saveData) return true;
  return false;
}

import {
  readTokens, SCREEN_W, SCREEN_H, createScreenCanvas,
  drawScreenshot, drawRecBadge, SCREEN_PAINTERS,
} from 'ourpwr-screens';


/* -------------------------------------------------------------------------
   Scene
   ------------------------------------------------------------------------- */

async function initScene(root) {
  const THREE = await import('three');

  const canvas = root.querySelector('[data-demo-canvas]');
  const sticky = root.querySelector('[data-demo-sticky]');
  const heroEl = root.querySelector('[data-demo-hero]');
  const copyEl = root.querySelector('.demo__copy');
  /* The track carries one extra viewport ahead of the beats for the intro, so
     scroll progress has to be split: the first 1/SEGMENTS drives the intro, the
     rest drives the walkthrough. */
  const SEGMENTS = BEAT_COUNT + 1;
  /* Set by resize(), consumed by the animation loop. */
  let wideX = 0, baseY = 0, introY = 0;
  const tokens = readTokens(root);

  /* No powerPreference and a 1.5x ceiling on the pixel ratio, both deliberate.
     'high-performance' asks for the discrete GPU by name; on a laptop that is a
     second GPU spun up for one decorative canvas, and inside an embedded
     browser view it is the request that most often loses the GPU process. The
     default lets the browser pick. 2x on a Retina panel means every frame is
     supersampled four times over and then MSAA'd on top; 1.5 costs a little
     edge crispness on the phone chassis and takes the fill rate down by 44%. */
  const renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: true,
    alpha: true,
  });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.08;

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(32, 1, 0.1, 100);
  camera.position.set(0, 0, 13);

  /* ---- Lighting ----
     A tiny procedural studio environment gives the metal something to reflect.
     Without it, increasing metalness only turns the frame grey or black. The
     texture is deliberately small: broad soft-box shapes are useful here, detail
     would be wasted and would cost memory on mobile. */
  const envCanvas = document.createElement('canvas');
  envCanvas.width = 128;
  envCanvas.height = 64;
  const envCtx = envCanvas.getContext('2d');
  const envWash = envCtx.createLinearGradient(0, 0, 0, envCanvas.height);
  envWash.addColorStop(0, '#fdfaff');
  envWash.addColorStop(0.48, '#aaa3c8');
  envWash.addColorStop(1, '#252335');
  envCtx.fillStyle = envWash;
  envCtx.fillRect(0, 0, envCanvas.width, envCanvas.height);
  /* Warm and cool soft boxes create the alternating highlights that make a
     titanium rail read as metal instead of a uniformly shaded grey extrusion. */
  const softBoxes = [
    [9, 5, 18, 54, 'rgba(255,247,235,0.96)'],
    [74, 2, 11, 60, 'rgba(255,255,255,0.78)'],
    [103, 9, 17, 46, 'rgba(198,205,255,0.78)'],
  ];
  softBoxes.forEach(([x, y, w, h, color]) => {
    envCtx.fillStyle = color;
    envCtx.fillRect(x, y, w, h);
  });
  const environment = new THREE.CanvasTexture(envCanvas);
  environment.mapping = THREE.EquirectangularReflectionMapping;
  environment.colorSpace = THREE.SRGBColorSpace;
  scene.environment = environment;
  scene.environmentIntensity = 1.15;

  /* Lower ambient energy preserves the dark glass / warm frame contrast. The
     directional sources then draw intentional highlights along opposite rails. */
  scene.add(new THREE.HemisphereLight(0xf8f3ff, 0x282638, 0.9));
  const key = new THREE.DirectionalLight(0xfffaf2, 3.4);
  key.position.set(3, 5, 6);
  scene.add(key);
  const rim = new THREE.DirectionalLight(0xb7adff, 2.35);
  rim.position.set(-5, 2, -4);
  scene.add(rim);
  const fill = new THREE.DirectionalLight(0xdce9ff, 1.1);
  fill.position.set(6, -2, 3);
  scene.add(fill);

  /* ---- Phone chassis ----
     Proportioned after a current-generation iPhone: 19.5:9 display, generous
     corner radius, thin flat rails, and a uniformly narrow bezel. Deliberately
     unbranded — no maker's marks anywhere on the model. */
  const PW = 3.0, PH = 6.5, PR = 0.60;
  const DEPTH = 0.22, BEVEL = 0.03;
  /* Front face is three concentric layers, outermost first:
       RIM    — the thin metallic edge of the rail, visible head-on
       BORDER — black glass surround; the display is not borderless
       screen — the lit display itself */
  const RIM = 0.03;
  const BORDER = 0.06;
  const INSET = RIM + BORDER;

  /* Rounded-rect / stadium shape, shared by the chassis, the screen, and the
     Dynamic Island so every radius on the device stays in the same family. */
  function roundedRect(w, h, r) {
    const s = new THREE.Shape();
    const x = w / 2, y = h / 2;
    s.moveTo(-x + r, -y);
    s.lineTo(x - r, -y);
    s.quadraticCurveTo(x, -y, x, -y + r);
    s.lineTo(x, y - r);
    s.quadraticCurveTo(x, y, x - r, y);
    s.lineTo(-x + r, y);
    s.quadraticCurveTo(-x, y, -x, y - r);
    s.lineTo(-x, -y + r);
    s.quadraticCurveTo(-x, -y, -x + r, -y);
    return s;
  }

  const body = new THREE.Mesh(
    new THREE.ExtrudeGeometry(roundedRect(PW, PH, PR), {
      depth: DEPTH, bevelEnabled: true, bevelSize: BEVEL,
      bevelThickness: BEVEL, bevelSegments: 4, curveSegments: 26,
    }),
    /* A restrained natural-titanium tone, not flat silver. The studio environment
       keeps the high metalness physically legible while the moderate roughness
       holds broad, premium highlights instead of chrome-like pinpricks. */
    new THREE.MeshPhysicalMaterial({
      color: 0x9a938b,
      roughness: 0.24,
      metalness: 0.88,
      clearcoat: 0.28,
      clearcoatRoughness: 0.16,
      envMapIntensity: 1.35,
    })
  );
  body.position.z = -DEPTH / 2;

  const phone = new THREE.Group();
  /* Scaled down rather than dollying the camera: the camera offset is what places
     the device against the copy, so changing it moves the composition as well as
     the size. Scale changes only the size. */
  phone.scale.setScalar(0.72);
  phone.add(body);

  /* ---- Side hardware ----
     Left rail, top to bottom: Action button, then the volume rocker. That order is
     the point, not decoration — the Action button is what a user reaches for to
     call for help, so the scene poses the phone left-side-on and presses it. */
  const railMat = new THREE.MeshPhysicalMaterial({
    color: 0xaaa39b,
    roughness: 0.16,
    metalness: 0.92,
    clearcoat: 0.38,
    clearcoatRoughness: 0.1,
    envMapIntensity: 1.5,
  });
  const actionMat = new THREE.MeshPhysicalMaterial({
    /* Slightly cooler than the titanium rail so the primary control is findable
       before it illuminates, without turning it into a toy-like coloured button. */
    color: 0x8b8295,
    roughness: 0.14,
    metalness: 0.82,
    clearcoat: 0.46,
    clearcoatRoughness: 0.08,
    envMapIntensity: 1.55,
    emissive: 0x5e5ea4,
    emissiveIntensity: 0.08,
  });
  function railButton(side, y, h, material = railMat, faceDepth = 0.18) {
    /* A shallow rounded extrusion resolves as a real pill-shaped hardware control
       at this camera distance. The old square boxes barely cleared the chassis and
       disappeared into the flat rail highlight. */
    const geometry = new THREE.ExtrudeGeometry(
      roundedRect(faceDepth, h, Math.min(0.055, h * 0.2)),
      {
        depth: 0.07,
        bevelEnabled: true,
        bevelSize: 0.012,
        bevelThickness: 0.012,
        bevelSegments: 3,
        curveSegments: 10,
      }
    );
    geometry.center();
    const b = new THREE.Mesh(geometry, material);
    b.rotation.y = Math.PI / 2;
    b.position.set(side * (PW / 2 + BEVEL - 0.004), y, 0);
    return b;
  }
  const actionButton = railButton(-1, PH * 0.29, 0.34, actionMat, 0.2);
  const volumeUp = railButton(-1, PH * 0.13, 0.46);
  const volumeDown = railButton(-1, PH * 0.13 - 0.56, 0.46);
  const powerButton = railButton(1, PH * 0.17, 0.78, railMat, 0.19);
  phone.add(actionButton, volumeUp, volumeDown, powerButton);
  const actionRestX = actionButton.position.x;

  /* The Press beat gets a small local light as well as emissive metal. That makes
     the activation visible on both the button cap and the adjacent titanium edge. */
  const actionLight = new THREE.PointLight(0xa1a1d3, 0, 1.6, 2);
  actionLight.position.set(actionRestX - 0.08, actionButton.position.y, 0.2);
  phone.add(actionLight);

  /* Fine antenna breaks are enough to interrupt the long, synthetic-looking rail
     without spending geometry on details that cannot resolve at this scale. */
  const antennaMat = new THREE.MeshStandardMaterial({
    color: 0x39363a, roughness: 0.7, metalness: 0.05,
  });
  const antennaBands = new THREE.InstancedMesh(
    new THREE.BoxGeometry(0.054, 0.055, DEPTH + 0.07),
    antennaMat,
    4
  );
  const antennaTransform = new THREE.Object3D();
  let antennaIndex = 0;
  [-1, 1].forEach((side) => {
    [-PH * 0.34, PH * 0.37].forEach((y) => {
      antennaTransform.position.set(side * (PW / 2 + 0.016), y, 0);
      antennaTransform.updateMatrix();
      antennaBands.setMatrixAt(antennaIndex++, antennaTransform.matrix);
    });
  });
  antennaBands.instanceMatrix.needsUpdate = true;
  phone.add(antennaBands);

  /* Press ripples: rings breaking off the Action button itself on the PRESS beat.
     They sit slightly proud of the rail so they read as coming from the hardware
     rather than from the screen. */
  const ripples = new THREE.Group();
  const rippleMats = [];
  /* Drawn as line loops, not ring meshes. A ring mesh has real width, so scaling it
     out to 5x scales the stroke with it and a hairline turns into a thick band by
     the end of its travel. A line stays one pixel wide at any radius, which is the
     constant-hairline look modern ripples use. */
  const circleGeo = new THREE.BufferGeometry();
  const RIPPLE_SEGMENTS = 96, circlePts = [];
  for (let i = 0; i < RIPPLE_SEGMENTS; i++) {
    const a = (i / RIPPLE_SEGMENTS) * Math.PI * 2;
    circlePts.push(Math.cos(a) * 0.16, Math.sin(a) * 0.16, 0);
  }
  circleGeo.setAttribute('position', new THREE.Float32BufferAttribute(circlePts, 3));

  for (let i = 0; i < 4; i++) {
    const mat = new THREE.LineBasicMaterial({
      /* Deep lavender, not white: the section ground is pale now, so a white
         hairline disappears into it. Mirrors --lav-2. */
      color: 0x5e5ea4,
      transparent: true, opacity: 0,
      /* Drawn on top of the chassis rather than depth-sorted against it. The rings
         are centred on the rail, so half of each one would otherwise be swallowed
         by the phone body and only the outer sliver would show. */
      depthTest: false, depthWrite: false,
    });
    rippleMats.push(mat);
    ripples.add(new THREE.LineLoop(circleGeo, mat));
  }
  /* Well clear of the chassis front face (DEPTH/2 + BEVEL = 0.14) so the rings
     read as emanating from the button rather than embedded in the body. */
  ripples.position.set(actionRestX, actionButton.position.y, 0.34);
  ripples.renderOrder = 10;
  phone.add(ripples);

  /* ---- Screen ---- */
  const screenCanvas = createScreenCanvas();
  const screenCtx = screenCanvas.getContext('2d');
  const screenTexture = new THREE.CanvasTexture(screenCanvas);
  screenTexture.colorSpace = THREE.SRGBColorSpace;

  /* Rounded to match the chassis. A square PlaneGeometry would push its corners
     outside the body once the bezel is this thin. */
  const screenGeo = new THREE.ShapeGeometry(
    roundedRect(PW - INSET * 2, PH - INSET * 2, PR - INSET), 26
  );
  /* ShapeGeometry emits UVs in local units, which tiles the texture. Remap to
     0..1 across the bounding box so the canvas fills the screen exactly once. */
  screenGeo.computeBoundingBox();
  const bb = screenGeo.boundingBox;
  const uvAttr = screenGeo.attributes.uv, posAttr = screenGeo.attributes.position;
  for (let i = 0; i < posAttr.count; i++) {
    uvAttr.setXY(
      i,
      (posAttr.getX(i) - bb.min.x) / (bb.max.x - bb.min.x),
      (posAttr.getY(i) - bb.min.y) / (bb.max.y - bb.min.y)
    );
  }
  uvAttr.needsUpdate = true;

  const screen = new THREE.Mesh(
    screenGeo,
    new THREE.MeshBasicMaterial({ map: screenTexture, toneMapped: false })
  );
  /* Must clear the chassis front face, which sits at (-DEPTH/2 + DEPTH + BEVEL).
     Anything at or below that is buried inside the body and the phone renders as
     a featureless slab — with no console error to explain it. */
  screen.position.z = DEPTH / 2 + BEVEL + 0.03;
  phone.add(screen);

  /* A second plane just above the screen cross-fades incoming beats. Textures
     used to hard-swap at beat boundaries, which read as a flicker; this layer
     fades the next screen in over ~0.4s, then becomes the base map. */
  const screenFade = new THREE.Mesh(
    screenGeo,
    new THREE.MeshBasicMaterial({ map: null, transparent: true, opacity: 0, toneMapped: false, depthWrite: false })
  );
  screenFade.position.z = screen.position.z + 0.002;
  screenFade.renderOrder = 1;
  screenFade.visible = false;
  phone.add(screenFade);
  let fadeTarget = null;
  let fadeStart = 0;

  /* A very light glass layer catches the key without muting the emissive display.
     It is separate from the screen texture so real screenshots get the same finish. */
  const screenGlass = new THREE.Mesh(
    screenGeo,
    /* Kept intentionally faint: every point of opacity here is a veil over the
       display and reads as a washed-out screen. Just enough survives for the
       glass to catch a moving glint as the phone rotates. */
    new THREE.MeshPhysicalMaterial({
      color: 0xdbe4ff,
      roughness: 0.03,
      metalness: 0,
      clearcoat: 1,
      clearcoatRoughness: 0.03,
      transparent: true,
      opacity: 0.028,
      depthWrite: false,
      envMapIntensity: 0.85,
    })
  );
  screenGlass.position.z = screen.position.z + 0.006;
  screenGlass.renderOrder = 2;
  phone.add(screenGlass);

  /* The earpiece lives in the narrow upper bezel, separate from the island. Its
     shallow inset is intentionally subtle: just enough to break the top glass edge. */
  const earpiece = new THREE.Mesh(
    new THREE.ShapeGeometry(roundedRect(0.5, 0.035, 0.0175), 10),
    new THREE.MeshBasicMaterial({ color: 0x000000, toneMapped: false })
  );
  earpiece.position.set(0, PH / 2 - 0.075, screen.position.z + 0.013);
  earpiece.renderOrder = 3;
  phone.add(earpiece);

  /* Pure-black sensor island, floated above the glass. Only built when the
     screens are procedural — real snapshots (data-screens) draw their own
     Dynamic Island, and a floated mesh would double up on top of it. */
  const ISLAND_W = 0.82;
  const ISLAND_H = 0.23;
  const ISLAND_Y = PH / 2 - 0.32;
  if (!root.dataset.screens) {
  const island = new THREE.Mesh(
    new THREE.ShapeGeometry(roundedRect(ISLAND_W, ISLAND_H, ISLAND_H / 2), 18),
    /* Unlit pure black: studio reflections and tone mapping must never lift the
       island toward grey or purple. The lens glint below provides the only colour. */
    new THREE.MeshBasicMaterial({ color: 0x000000, toneMapped: false })
  );
  island.position.set(0, ISLAND_Y, screen.position.z + 0.012);
  island.renderOrder = 3;
  phone.add(island);

  /* The left sensor chamber is only a whisper above black, matching how it recedes
     inside the island rather than reading as a separate grey dot. */
  const sensorChamber = new THREE.Mesh(
    new THREE.ShapeGeometry(roundedRect(0.31, 0.13, 0.065), 14),
    new THREE.MeshBasicMaterial({ color: 0x020203, toneMapped: false })
  );
  sensorChamber.position.set(-0.15, ISLAND_Y, island.position.z + 0.002);
  sensorChamber.renderOrder = 4;
  phone.add(sensorChamber);

  /* Camera glass occupies the right end of the capsule. It stays nearly black;
     only the coated inner pupil and pin-point highlight carry cool colour. */
  const cameraLens = new THREE.Mesh(
    new THREE.CircleGeometry(0.074, 24),
    new THREE.MeshPhysicalMaterial({
      color: 0x010105,
      roughness: 0.06,
      metalness: 0.12,
      clearcoat: 1,
      clearcoatRoughness: 0.01,
      envMapIntensity: 0.8,
    })
  );
  cameraLens.position.set(0.275, ISLAND_Y, island.position.z + 0.003);
  cameraLens.renderOrder = 4;
  phone.add(cameraLens);

  const cameraPupil = new THREE.Mesh(
    new THREE.CircleGeometry(0.038, 18),
    new THREE.MeshBasicMaterial({ color: 0x050817, toneMapped: false })
  );
  cameraPupil.position.set(cameraLens.position.x, ISLAND_Y, cameraLens.position.z + 0.002);
  cameraPupil.renderOrder = 5;
  phone.add(cameraPupil);

  /* A pin-point blue reflection is the only colour inside the otherwise black
     island, like the glint on a coated camera lens rather than an LED indicator. */
  const lensGlint = new THREE.Mesh(
    new THREE.CircleGeometry(0.011, 12),
    new THREE.MeshBasicMaterial({
      color: 0x5ca8ff,
      transparent: true,
      opacity: 0.9,
      toneMapped: false,
      depthWrite: false,
    })
  );
  lensGlint.position.set(
    cameraLens.position.x + 0.013,
    cameraLens.position.y + 0.013,
    cameraPupil.position.z + 0.002
  );
  lensGlint.renderOrder = 6;
  phone.add(lensGlint);

  const sensor = new THREE.Mesh(
    new THREE.CircleGeometry(0.022, 16),
    new THREE.MeshBasicMaterial({ color: 0x09090b, toneMapped: false })
  );
  sensor.position.set(-0.12, ISLAND_Y, sensorChamber.position.z + 0.002);
  sensor.renderOrder = 5;
  phone.add(sensor);
  }

  /* ---- Bezel lip ----
     A bevelled ring sitting proud of the screen, so the glass reads as recessed
     into the body instead of floating on top of it. The chassis is a solid
     extrusion and can't have a well cut into it, so the inset is faked by raising
     the surround rather than sinking the screen — the screen still has to clear
     the chassis front face (see failure mode 2 in CLAUDE.md).

     Roughness is very low here on purpose: this ring is the edge that catches the
     specular glint and sells the device as a physical object. */
  /* Black glass surround. The display is not edge-to-edge, so a black border sits
     between the metal rim and the lit area — without it the screen appears to run
     straight into the rail and the device reads as a toy. */
  const glass = new THREE.Mesh(
    new THREE.ShapeGeometry(roundedRect(PW - RIM * 2, PH - RIM * 2, PR - RIM), 26),
    /* Unlit true black. As a physical material the studio reflections lifted this
       border toward grey, and next to a real screenshot it read as a haze rather
       than the dead bezel of a modern display. */
    new THREE.MeshBasicMaterial({ color: 0x000000, toneMapped: false })
  );
  glass.position.z = screen.position.z - 0.012;
  phone.add(glass);

  /* Metallic rim: only RIM wide, a hairline of polished rail visible head-on.
     Very low roughness — this edge is what catches the specular glint. */
  const lipShape = roundedRect(PW, PH, PR);
  lipShape.holes.push(roundedRect(PW - RIM * 2, PH - RIM * 2, PR - RIM));
  const lip = new THREE.Mesh(
    new THREE.ExtrudeGeometry(lipShape, {
      depth: 0.016, bevelEnabled: true, bevelSize: 0.008,
      bevelThickness: 0.008, bevelSegments: 3, curveSegments: 26,
    }),
    new THREE.MeshPhysicalMaterial({
      color: 0xb8b0a7,
      roughness: 0.13,
      metalness: 0.94,
      clearcoat: 0.42,
      clearcoatRoughness: 0.08,
      envMapIntensity: 1.65,
    })
  );
  lip.position.z = DEPTH / 2 + BEVEL;
  phone.add(lip);

  scene.add(phone);

  /* ---- Optional real snapshots ---- */
  /* data-screens='["url","url",...]' — index matches BEAT. Any missing entry
     falls back to the procedural painter, so a partial set is fine. */
  const snapshotTextures = new Array(BEAT_COUNT).fill(null);
  let witnessImg = null;
  const declared = root.dataset.screens;
  if (declared) {
    try {
      const urls = JSON.parse(declared);
      const loader = new THREE.TextureLoader();
      urls.forEach((url, i) => {
        if (!url || i >= BEAT_COUNT) return;
        /* The witness screen is repainted every tick so the recording emblem can
           blink over it, so it is loaded as an image and left out of the
           texture table — a static texture cannot carry a live overlay. */
        if (i === BEAT.WITNESS) {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = () => { witnessImg = img; };
          img.src = url;
          return;
        }
        loader.load(url, (tex) => {
          tex.colorSpace = THREE.SRGBColorSpace;
          snapshotTextures[i] = tex;
        });
      });
    } catch (e) {
      /* Malformed JSON is not worth breaking the page over. */
    }
  }

  /* ---- Aurora: emotional weather behind the phone ----
     Replaces the old orbiting node spheres, which read as decoration. One soft
     radial wash in three moods, cross-faded by the story: a calm breath while
     nothing happens, a gathered and saturated core as the alert goes out, and a
     warm settle as the circle responds. */
  function auroraTexture(inner, mid, edge) {
    const c = document.createElement('canvas');
    c.width = c.height = 512;
    const g = c.getContext('2d');
    const grad = g.createRadialGradient(256, 256, 0, 256, 256, 256);
    grad.addColorStop(0, inner);
    grad.addColorStop(0.45, mid);
    grad.addColorStop(1, edge);
    g.fillStyle = grad;
    g.fillRect(0, 0, 512, 512);
    const tex = new THREE.CanvasTexture(c);
    tex.colorSpace = THREE.SRGBColorSpace;
    return tex;
  }
  const auroraGroup = new THREE.Group();
  const auroraMats = [];
  const AURORA_MOODS = [
    /* calm: brand lavender, barely there */
    auroraTexture('rgba(199,195,236,0.9)', 'rgba(161,161,211,0.34)', 'rgba(161,161,211,0)'),
    /* alert: saturated lavender — the middle of the story runs hot while the
       alert is live. It escalates by SATURATION, not by hue: this is
       --violet-500, the site's own accent, against the calm mood's desaturated
       grey-lavender either side of it. No alarm colour is imported. */
    auroraTexture('rgba(219,203,252,0.92)', 'rgba(124,92,224,0.38)', 'rgba(124,92,224,0)'),
    /* settle: back to lavender as the circle responds and the night closes out */
    auroraTexture('rgba(208,202,240,0.9)', 'rgba(172,168,220,0.35)', 'rgba(172,168,220,0)'),
  ];
  AURORA_MOODS.forEach((tex) => {
    /* depthWrite off so the three mood layers stack, but depth TEST stays on —
       without it the transparent pass paints the wash over the phone even
       though it sits behind it. */
    const mat = new THREE.MeshBasicMaterial({
      map: tex, transparent: true, opacity: 0, toneMapped: false,
      depthWrite: false,
    });
    auroraMats.push(mat);
    auroraGroup.add(new THREE.Mesh(new THREE.PlaneGeometry(15, 15), mat));
  });
  auroraGroup.position.set(0, 0.4, -3.2);
  auroraGroup.renderOrder = -1;
  scene.add(auroraGroup);

  /* ---- Sizing ---- */
  function resize() {
    const w = sticky.clientWidth;
    const h = sticky.clientHeight;
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    /* Below the same 60rem breakpoint used by the CSS, the copy occupies the
       lower portion of the viewport. Key this framing to width rather than an
       aspect-ratio guess so narrow desktop windows and near-square panes cannot
       accidentally receive the much tighter wide-screen camera. */
    const compact = w < 960;
    phone.scale.setScalar(compact ? 0.90 : 0.72);
    camera.position.z = compact ? 21 : 13;
    /* Two vertical framings, eased between across the intro. introY raises the
       camera so the device stays below the complete hero stack (including its
       CTA) and crops at the bottom edge; baseY is where it settles for the
       walkthrough. The extra intro clearance is intentional: at the common
       1200 x 900 preview size, 2.5 world units put the phone's top edge directly
       behind the CTA. */
    /* Compact: past ~900px tall the copy rises off the bottom edge (the 14svh
       term in ourpwr-demo.css), so the camera drops with it and the phone lifts
       by the same amount. Phones are under that height and keep the framing they
       had; iPad portrait, where a fixed -1.2 stranded the text well below the
       device, gets the whole ramp. */
    /* -1.55 rather than -1.2: even with the beats hugging the bottom of their
       band, the longest beat still reached the phone's bottom edge on a handset.
       This is the last ~20px of clearance it needed. */
    const tallLift = Math.min(0.7, Math.max(0, (h - 900) / 400));
    baseY = compact ? -1.55 - tallLift : 0;
    introY = compact ? 3.1 : 3.3;
    /* Past 60rem the CSS moves the copy into the right half, so the camera slides
       right to push the phone into the left half to meet it. Stored rather than
       applied here: the animation loop eases toward it across the intro, so the
       phone starts centred under the headline. */
    wideX = compact ? 0 : 1.6;
    camera.updateProjectionMatrix();
  }
  resize();
  window.addEventListener('resize', resize, { passive: true });

  /* ---- Browser chrome ----
     Safari and Chrome on Android tint their toolbars from <meta name="theme-color">.
     The bars cannot be reclaimed — only a home-screen app gets that — but they can
     stop framing the night in white, which is what makes the walkthrough feel
     boxed in on a phone. See the border-color note in ourpwr-demo.css. */
  const barMeta = document.querySelector('meta[name="theme-color"]');
  const barDay = barMeta ? barMeta.getAttribute('content') : '';
  const barNightColor = getComputedStyle(sticky).borderTopColor;
  let barIsNight = false;

  /* ---- Scroll → progress ---- */
  let progress = 0;
  function computeProgress() {
    const rect = root.getBoundingClientRect();
    const scrollable = rect.height - window.innerHeight;
    if (scrollable <= 0) return 0;
    return Math.min(1, Math.max(0, -rect.top / scrollable));
  }

  /* ---- Beat copy activation ---- */
  const beatEls = Array.from(root.querySelectorAll('[data-beat]'));
  const stepEls = Array.from(root.querySelectorAll('.demo__step'));
  let activeBeat = -1;
  function setActiveBeat(i) {
    if (i === activeBeat) return;
    activeBeat = i;
    beatEls.forEach((el, idx) => el.classList.toggle('is-active', idx === i));
    stepEls.forEach((el, idx) => el.setAttribute('aria-current', idx === i ? 'true' : 'false'));
  }

  /* ---- Render loop ---- */
  const damp = (current, target, lambda, dt) =>
    current + (target - current) * (1 - Math.exp(-lambda * dt));

  let smoothed = 0;
  let lastTime = performance.now();
  let lastScreenPaint = 0;

  function frame(now) {
    rafId = requestAnimationFrame(frame);

    const dt = Math.min(0.05, (now - lastTime) / 1000);
    lastTime = now;

    /* Off-screen: keep the loop alive but do no work. rAF is already throttled
       to near-zero by the browser when the tab is hidden. */
    if (!isNear(root, 100)) return;

    progress = computeProgress();
    smoothed = damp(smoothed, progress, 6, dt);

    /* Intro occupies the first segment; beats share the rest. */
    const introT = Math.min(1, Math.max(0, smoothed * SEGMENTS));
    const beatProg = Math.min(1, Math.max(0, (smoothed * SEGMENTS - 1) / BEAT_COUNT));

    /* Hero clears out well before the intro segment ends — a linear fade across the
       whole segment leaves the headline lingering over the phone for most of the
       travel. Gone by 40%, with a small lift so it reads as leaving rather than
       dimming. Beat copy is held back until the intro is fully over so the two
       never sit on top of each other. */
    const heroOut = Math.min(1, introT / 0.4);
    if (heroEl) {
      heroEl.style.opacity = String(1 - heroOut);
      heroEl.style.transform = 'translateY(' + (-heroOut * 40).toFixed(1) + 'px)';
    }
    if (copyEl) copyEl.style.opacity = String(introT);

    const scaled = beatProg * BEAT_COUNT;
    const beatIndex = Math.min(BEAT_COUNT - 1, Math.floor(scaled));
    const localT = scaled - beatIndex;
    setActiveBeat(beatIndex);

    /* Camera slides from centred to offset, which is what walks the phone from the
       middle of the hero over to the left where the beat copy meets it. */
    camera.position.x = wideX * introT;
    camera.position.y = introY + (baseY - introY) * introT;

    /* Drives the background wash and the grain drift from CSS. Written as a plain
       number so calc() can multiply it by a length or a percentage. */
    sticky.style.setProperty('--demo-wash', smoothed.toFixed(4));

    /* Story darkness. Night belongs to the first beat and nothing else: the
       ground falls dark as "You encountered someone unsafe" settles and is
       back to the pale page by the press. Both windows are deliberately tight
       and centred on a beat boundary: a beat that dwells mid-transition strands
       its copy on a mid-grey ground where neither ink can read, but at the
       boundary the copy is cross-fading anyway. */
    const darkIn = Math.min(1, Math.max(0, (scaled - 0.05) / 0.25));
    const darkOut = Math.min(1, Math.max(0, (scaled - BEAT.PRESS + 0.3) / 0.6));
    const darkK = darkIn * (1 - darkOut) * introT;
    sticky.style.setProperty('--demo-dark', darkK.toFixed(4));

    /* Toolbars follow. Flipped on a threshold with a dead band, not tracked
       continuously: the browser re-tints with its own fade, and restarting that
       every frame is the flicker this is meant to avoid. */
    if (barMeta) {
      const wantNight = darkK > (barIsNight ? 0.45 : 0.6);
      if (wantNight !== barIsNight) {
        barIsNight = wantNight;
        barMeta.setAttribute('content', wantNight ? barNightColor : barDay);
      }
    }

    /* The copy ink rides a sharpened version of the same value, so it spends as
       little scroll as possible in the unreadable middle of the cross-fade. */
    const k3 = darkK ** 3;
    sticky.style.setProperty('--demo-ink',
      (k3 / (k3 + (1 - darkK) ** 3 || 1)).toFixed(4));

    /* Slide 1 only: the outside pulls darker and the light gathers around the
       phone. Peaks mid-beat and is gone by the press, so nothing else in the
       story is touched. */
    const slide1K = Math.max(0, 1 - Math.abs(scaled - 0.55) / 0.55) * introT;
    sticky.style.setProperty('--demo-slide1', slide1K.toFixed(4));

    /* Phone motion: a slow settle, never a spin. Rotation is subtle enough
       to read as "held in a hand" rather than a product turntable. */
    /* Positive rotation.y faces the device to the right, toward the copy, which
       brings the left rail — and the Action button on it — around to the camera.
       It only relaxes as the story moves off the hardware and onto the circle. */
    /* On top of that settle, a slow scroll-keyed wander: a few degrees of yaw,
       pitch and roll that land differently on every beat, so travelling the
       story reads as the phone being handled rather than locked on a stand.
       Keyed to scroll, not time — it only turns when the visitor moves, and
       the damp below keeps it drifting rather than tracking. The frequencies
       are unrelated on purpose, so no two beats share a pose. */
    const targetRotY = 0.50 - beatProg * 0.26 + Math.sin(scaled * 1.7) * 0.07;
    const targetRotX = 0.09 - beatProg * 0.16 + Math.cos(scaled * 1.3) * 0.035;
    phone.rotation.y = damp(phone.rotation.y, targetRotY, 4, dt);
    phone.rotation.x = damp(phone.rotation.x, targetRotX, 4, dt);
    phone.rotation.z = damp(phone.rotation.z, Math.sin(scaled * 1.1) * 0.022, 4, dt);
    phone.position.y = damp(phone.position.y, Math.sin(now / 2600) * 0.06, 3, dt);

    /* The press itself: a single inward push on the PRESS beat, eased in and
       released, so the hardware visibly does what the copy claims. Inward is +X
       because the button sits on the -X rail. */
    const pressT = Math.min(1, Math.max(0, scaled - BEAT.PRESS));
    const pressGlow = pressT > 0 && pressT < 1 ? Math.sin(pressT * Math.PI) : 0;
    actionButton.position.x =
      actionRestX + Math.sin(Math.min(1, pressT / 0.55) * Math.PI) * 0.042;
    /* Emissive intensity makes the button itself turn lavender; the point light
       carries the same pulse onto the rail so it remains obvious at small sizes. */
    actionMat.emissiveIntensity = 0.08 + pressGlow * 3.2;
    actionLight.intensity = pressGlow * 7.5;
    actionLight.position.x = actionButton.position.x - 0.08;

    /* Rings stagger outward from the button and fade as they grow. Held at zero
       outside the beat so nothing lingers on screen after the moment has passed. */
    const pressing = pressT > 0 && pressT < 1;
    ripples.children.forEach((ring, i) => {
      const k = Math.min(1, Math.max(0, pressT * 1.55 - i * 0.2));
      ring.scale.setScalar(0.45 + k * 5.2);
      /* Hold near full opacity through the first half of the travel, then fall
         away — a linear fade from the outset reads as washed out rather than as a
         pulse leaving the button. */
      rippleMats[i].opacity = pressing ? Math.min(1, (1 - k) * 1.9) * 0.95 : 0;
    });

    /* Aurora choreography. alertK rises as the alert goes out and the circle is
       told; warmK takes over on the response beat. The calm mood owns whatever
       strength the other two haven't claimed, so the wash never doubles up. */
    const alertK = Math.min(1, Math.max(0, (scaled - BEAT.PRESS - 0.3) / 0.9));
    const warmK = Math.min(1, Math.max(0, (scaled - BEAT.RESPONSE) / 0.7));
    const breathe = 1 + Math.sin(now / 2600) * 0.045 + Math.sin(now / 6100) * 0.03;
    /* The alert gathers the light inward; the response lets it open back out. */
    auroraGroup.scale.setScalar((1 - alertK * 0.2 + warmK * 0.16) * breathe);
    auroraGroup.rotation.z = now / 47000;
    /* The lavender moods are for the pale ground only — against the night they
       hang behind the phone as a milky cast, and the nightglow field already
       owns beat one's colour. The exception is slide 1's own gather, which is
       the light coming out of the phone and is allowed to burn against it. */
    auroraMats[0].opacity = introT * 0.52 * (1 - alertK) * (1 - darkK) + slide1K * 0.6;
    /* While the alert is live the pink carries a slow pulse — a swell at
       roughly breathing rate, never a strobe. The section's first rule is to
       show urgency without distressing, so the trough stays well above zero
       and the rate stays far below anything alarm-like.
       2600 is the same clock the phone's float and the aurora's breathe run on;
       260 was a dropped zero, and a 1.6-second swell on a full-screen wash reads
       on a phone as the screen brightness pumping up and down. */
    const alertPulse = 0.90 + 0.10 * Math.sin(now / 2600);
    /* On the darkened ground the pink can afford more presence — it reads as a
       glow against night rather than a stain on the pale wash. */
    auroraMats[1].opacity =
      introT * (0.66 + darkK * 0.22) * alertK * (1 - warmK) * alertPulse;
    auroraMats[2].opacity = introT * 0.6 * warmK * (1 - darkK);

    /* Screen: repaint at ~20fps, which is plenty for a texture this size and
       keeps the main thread free on mobile. */
    if (now - lastScreenPaint > 50) {
      lastScreenPaint = now;
      const snapshot = snapshotTextures[beatIndex];
      if (!snapshot) {
        if (beatIndex === BEAT.WITNESS && witnessImg) {
          drawScreenshot(screenCtx, witnessImg);
          drawRecBadge(screenCtx, now / 1000);
        } else {
          screenCtx.clearRect(0, 0, SCREEN_W, SCREEN_H);
          SCREEN_PAINTERS[beatIndex](screenCtx, tokens, localT * 4 + now / 1000);
        }
        screenTexture.needsUpdate = true;
      }
      const targetMap = snapshot || screenTexture;
      if (targetMap !== screen.material.map && targetMap !== fadeTarget) {
        fadeTarget = targetMap;
        fadeStart = now;
        screenFade.material.map = targetMap;
        screenFade.material.needsUpdate = true;
        screenFade.material.opacity = 0;
        screenFade.visible = true;
      }
    }

    /* Advance the cross-fade every frame, not just on repaint ticks. */
    if (fadeTarget) {
      const raw = Math.min(1, (now - fadeStart) / 420);
      const eased = raw * raw * (3 - 2 * raw);
      screenFade.material.opacity = eased;
      if (raw === 1) {
        screen.material.map = fadeTarget;
        screen.material.needsUpdate = true;
        screenFade.visible = false;
        screenFade.material.opacity = 0;
        fadeTarget = null;
      }
    }

    renderer.render(scene, camera);
  }

  /* ---- Lifecycle ----
     A WebGL context is a process-wide resource, not a page-local one. The
     browser keeps a hard cap on how many are live at once and starts dropping
     the oldest past it, and a context lost inside an embedded browser view
     takes the whole view down rather than firing a recoverable event. Every
     reload of this page asks for a fresh one, so the old one has to go back.

     Three things are needed and none of them were here: stop the loop, hand
     back the GPU memory the scene holds, and drop the context itself. */
  let rafId = requestAnimationFrame(frame);
  let destroyed = false;

  function stopLoop() {
    if (rafId) cancelAnimationFrame(rafId);
    rafId = 0;
  }

  function startLoop() {
    if (destroyed || rafId) return;
    /* Reset the clock: dt is already clamped, but resuming from a long pause
       with a stale timestamp burns the first frame on a clamped step. */
    lastTime = performance.now();
    rafId = requestAnimationFrame(frame);
  }

  function destroy() {
    if (destroyed) return;
    destroyed = true;
    stopLoop();
    /* Geometries and materials are not garbage collected — they hold GPU
       handles that only dispose() releases. Textures hang off the materials,
       so they are walked here rather than tracked separately. */
    scene.traverse((obj) => {
      if (obj.geometry) obj.geometry.dispose();
      const mats = Array.isArray(obj.material) ? obj.material : obj.material ? [obj.material] : [];
      mats.forEach((m) => {
        Object.values(m).forEach((v) => {
          if (v && v.isTexture) v.dispose();
        });
        m.dispose();
      });
    });
    renderer.dispose();
    /* dispose() releases Three's own state but leaves the context alive and
       counting against the cap. forceContextLoss is what actually returns it. */
    const ctx = renderer.getContext();
    if (ctx) {
      const lose = ctx.getExtension('WEBGL_lose_context');
      if (lose) lose.loseContext();
    }
  }

  /* pagehide, not unload: unload never fires on a page restored from the
     back/forward cache, and it is ignored outright on iOS. */
  window.addEventListener('pagehide', destroy);
  /* The loop's own off-screen check assumed a hidden tab gets rAF throttled to
     near zero. That holds for a background browser tab and not for an embedded
     preview pane, which keeps painting. Stop it outright instead. */
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) stopLoop();
    else startLoop();
  });

  root.classList.add('demo--live');
  canvas.setAttribute('data-ready', '');
}

/* -------------------------------------------------------------------------
   Boot
   ------------------------------------------------------------------------- */

function boot(root) {
  if (prefersReducedMotion() || !hasWebGL() || isLowPowered()) {
    root.classList.add('demo--fallback');
    return;
  }

  /* Step dots. The walkthrough gave no sign of how long it was or that it
     could be moved through by anything but scrolling. Built here rather than in
     Liquid because they are meaningless without the scene — the fallback has
     every beat on screen at once already. */
  const beats = root.querySelectorAll('[data-beat]');
  const sticky = root.querySelector('[data-demo-sticky]');
  if (beats.length && sticky) {
    const nav = document.createElement('nav');
    nav.className = 'demo__steps';
    nav.setAttribute('aria-label', 'Walkthrough steps');
    beats.forEach((beat, i) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'demo__step';
      dot.setAttribute('aria-current', 'false');
      const heading = beat.querySelector('.demo__heading');
      dot.setAttribute('aria-label',
        'Step ' + (i + 1) + (heading ? ': ' + heading.textContent.trim() : ''));
      /* Land mid-beat: the same split the render loop uses, one intro segment
         then one segment per beat. */
      dot.addEventListener('click', () => {
        const scrollable = root.offsetHeight - window.innerHeight;
        if (scrollable <= 0) return;
        const top = root.getBoundingClientRect().top + window.scrollY;
        window.scrollTo({
          top: top + scrollable * ((i + 1.5) / (beats.length + 1)),
          behavior: prefersReducedMotion() ? 'auto' : 'smooth'
        });
      });
      nav.appendChild(dot);
    });
    sticky.appendChild(nav);
  }

  /* Defer the 671KB Three.js payload until the section is genuinely near, but
     never let a missed event mean it loads at all. */
  let booted = false;
  function tryInit() {
    if (booted || !isNear(root, 400)) return;
    booted = true;
    window.removeEventListener('scroll', tryInit);
    window.removeEventListener('resize', tryInit);
    initScene(root).catch(() => root.classList.add('demo--fallback'));
  }

  window.addEventListener('scroll', tryInit, { passive: true });
  window.addEventListener('resize', tryInit, { passive: true });
  tryInit();
}

document.querySelectorAll('[data-demo]').forEach(boot);
